<template>
  <div>
    <v-dialog :value="value" max-width="1050" scrollable @input="$emit('input', $event)">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ $t('personInfo.title') }}
          <v-spacer />
          <v-btn icon :aria-label="$t('personInfo.close')" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="person-overview-body">
          <v-progress-linear v-if="loading" indeterminate />
          <v-alert v-if="error" type="error" text dense>{{ error }}<v-btn icon :aria-label="$t('personInfo.retry')" @click="load(personId)"><v-icon>mdi-refresh</v-icon></v-btn></v-alert>
          <template v-if="info && !loading">
            <v-alert v-for="pending in info.pending_merges" :key="pending.id" type="warning" text dense>
              {{ $t('personInfo.merge.pending', { username: pending.discarded_username || 'LDAP' }) }}
              <v-btn small text :loading="retrying === pending.id" @click="retry(pending.id)"><v-icon small left>mdi-refresh</v-icon>{{ $t('personInfo.retry') }}</v-btn>
            </v-alert>
            <div class="person-heading">
              <v-avatar size="86" color="grey lighten-3"><v-img v-if="photoUrl" :src="photoUrl" :alt="name" /><v-icon v-else size="44" color="grey">mdi-account</v-icon></v-avatar>
              <div class="person-heading-text"><h2 class="title">{{ name }}</h2><div class="text-caption grey--text">#{{ info.id }}</div><a v-for="email in info.emails" :key="email" :href="'mailto:' + email" class="person-email">{{ email }}</a><div v-if="!info.emails.length">{{ $t('personInfo.noEmail') }}</div></div>
            </div>
            <v-row class="my-2">
              <v-col cols="12" sm="4">
                <h3 class="subtitle-1 mb-2">{{ $t('personInfo.incharge') }}</h3>
                <div v-for="person in info.incharge" :key="person.id" class="mb-1"><a :href="'/manage/edit/researchers/edit/' + person.id" target="_blank" rel="noopener noreferrer">{{ person.preferred_name.given_name }} {{ person.preferred_name.family_name }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon></a></div>
                <span v-if="!info.incharge.length">{{ $t('personInfo.none') }}</span>
              </v-col>
              <v-col cols="12" sm="8">
                <h3 class="subtitle-1 mb-2">{{ $t('table.account') }}</h3>
                <div class="mb-2"><v-icon small :color="indicator.color" class="mr-2">{{ indicator.icon }}</v-icon>{{ $t('table.tooltip.' + indicator.tooltip) }}</div>
                <dl v-if="info.account" class="person-account">
                  <dt>{{ $t('form.username') }}</dt><dd>{{ info.account.username || $t('personInfo.none') }}</dd>
                  <dt>UID</dt><dd>{{ info.account.uid }}</dd>
                  <dt>{{ $t('form.expiration_date') }}</dt><dd>{{ date(info.account.expiration_date) }}</dd>
                  <dt>{{ $t('personInfo.services') }}</dt><dd>{{ info.account.services.map(service => service.name).join(', ') || $t('personInfo.none') }}</dd>
                </dl>
                <p v-else>{{ $t('personInfo.noAccount') }}</p>
              </v-col>
            </v-row>
            <v-divider />
            <v-tabs v-model="tab" class="mt-3" show-arrows><v-tab>{{ $t('personInfo.vinculations') }} ({{ info.vinculations.length }})</v-tab><v-tab>{{ $t('personInfo.related') }} ({{ info.relation_count }})</v-tab><v-tab>{{ $t('personInfo.duplicates.title') }}{{ duplicatesLoaded ? ' (' + duplicates.length + ')' : '' }}</v-tab></v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <p v-if="!info.vinculations.length" class="py-4">{{ $t('personInfo.noVinculations') }}</p>
                <section v-for="period in ['active', 'future', 'past']" :key="period" class="pt-4">
                  <template v-if="vinculations(period).length">
                    <h3 class="subtitle-1 mb-2">{{ $t('personInfo.periods.' + period) }}</h3>
                    <div v-for="relation in vinculations(period)" :key="relation.id" class="person-vinculation">
                      <div><strong>{{ $t('vinculation.types.' + relation.type) }}</strong><div>{{ relation.category ? localized(relation.category.name) : '' }}</div></div>
                      <span>{{ date(relation.start_date) }} - {{ date(relation.end_date) }}</span>
                    </div>
                  </template>
                </section>
              </v-tab-item>
              <v-tab-item>
                <p v-if="!info.relations.length" class="py-4">{{ $t('personInfo.noRelations') }}</p>
                <section v-for="group in info.relations" :key="group.kind" class="pt-4">
                  <h3 class="subtitle-1 mb-2">{{ $t('personInfo.kinds.' + group.kind) }} ({{ group.items.length }})</h3>
                  <div v-for="item in group.items" :key="item.id" class="person-related">
                    <a :href="item.url" target="_blank" rel="noopener noreferrer">{{ localized(item.title) || '#' + item.id }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon></a>
                    <span class="text-caption grey--text text--darken-1">{{ item.roles.map(roleLabel).join(' · ') }}</span>
                  </div>
                </section>
              </v-tab-item>
              <v-tab-item>
                <v-progress-linear v-if="duplicatesLoading" indeterminate class="mt-4" />
                <v-alert v-if="duplicatesError" type="error" text dense class="mt-4">{{ duplicatesError }}<v-btn icon :aria-label="$t('personInfo.retry')" @click="loadDuplicates"><v-icon>mdi-refresh</v-icon></v-btn></v-alert>
                <v-data-table v-if="duplicatesLoaded" :headers="duplicateHeaders" :items="duplicates" :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 30, -1], itemsPerPageText: $t('table.itemsPerPageText') }" :no-data-text="$t('personInfo.duplicates.empty')" class="mt-3" disable-sort>
                  <template v-slot:item.name="{ item }">
                    <div class="person-duplicate-heading"><v-avatar size="36" color="grey lighten-3"><v-img v-if="item.photo" :src="mediaUrl(item.photo)" :alt="duplicateName(item)" /><v-icon v-else small>mdi-account</v-icon></v-avatar><div class="person-heading-text"><strong>{{ duplicateName(item) }}</strong><div class="text-caption">#{{ item.id }} · {{ item.username || $t('table.tooltip.account_no-username') }}</div><a v-for="email in item.emails" :key="email" :href="'mailto:' + email" class="person-email text-caption">{{ email }}</a></div></div>
                  </template>
                  <template v-slot:item.reasons="{ item }">
                    <strong class="text-caption">{{ $t('personInfo.duplicates.levels.' + item.confidence) }}</strong>
                    <div v-for="reason in item.reasons" :key="reason" class="text-caption">{{ $t('personInfo.duplicates.reasons.' + reason) }}</div>
                    <div v-for="conflict in item.conflicts" :key="conflict" class="text-caption error--text"><v-icon small color="error">mdi-alert-outline</v-icon> {{ $t('personInfo.duplicates.conflicts.' + conflict) }}</div>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn icon text :href="'/manage/edit/researchers/edit/' + item.id" target="_blank" rel="noopener noreferrer" :title="$t('personInfo.duplicates.inspect')" :aria-label="$t('personInfo.duplicates.inspect')"><v-icon>mdi-open-in-new</v-icon></v-btn>
                    <v-btn icon text color="primary" :title="$t('personInfo.merge.title')" :aria-label="$t('personInfo.merge.title')" @click="startMerge(item.id)"><v-icon>mdi-account-switch-outline</v-icon></v-btn>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </template>
        </v-card-text>
        <v-card-actions class="person-overview-actions">
          <v-btn text :disabled="loading || !info" @click="startMerge(null)"><v-icon left small>mdi-account-switch-outline</v-icon>{{ $t('personInfo.merge.title') }}</v-btn>
          <v-spacer />
          <v-btn text @click="$emit('input', false)">{{ $t('personInfo.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <PersonMergeDialog v-if="info" v-model="mergeDialog" :person="info" :people="people" :initial-other-id="duplicateToMerge" @merged="merged" />
  </div>
</template>

<script>
import axios from 'axios';
import { accountIndicator } from '../../utils/accountStatus';
import PersonMergeDialog from './PersonMergeDialog.vue';

export default {
  components: { PersonMergeDialog },
  props: { value: Boolean, personId: [Number, String], people: { type: Array, default: () => [] } },
  data: () => ({ info: null, loading: false, error: '', tab: 0, mergeDialog: false, retrying: null, requestId: 0, duplicates: [], duplicatesLoaded: false, duplicatesLoading: false, duplicatesError: '', duplicateToMerge: null }),
  computed: {
    duplicateHeaders() { return [{ text: this.$t('name'), value: 'name' }, { text: this.$t('personInfo.duplicates.matches'), value: 'reasons' }, { text: this.$t('actions'), value: 'actions', align: 'end', width: '110px' }]; },
    activePersonId() { return this.value ? this.personId : null; },
    name() { return this.info ? [this.info.name.given_name, this.info.name.family_name].filter(Boolean).join(' ') : ''; },
    photoUrl() {
      if (!this.info || !this.info.photo) return null;
      const path = this.info.photo;
      return /^https?:\/\//.test(path) ? path : this.$store.state.endpoint.replace(/\/$/, '') + path;
    },
    indicator() {
      const account = this.info && this.info.account;
      const limit = new Date(); limit.setDate(limit.getDate() + 30);
      return accountIndicator(account ? { has_account: true, has_username: !!String(account.username || '').trim(), active_account: account.activation_status, expires_soon: !!account.expiration_date && new Date(account.expiration_date) <= limit } : null);
    }
  },
  watch: {
    tab(index) { if (index === 2 && this.info && !this.duplicatesLoaded) this.loadDuplicates(); },
    activePersonId(id) {
      if (id) { this.tab = 0; this.load(id); }
      else { this.requestId++; this.mergeDialog = false; }
    }
  },
  methods: {
    mediaUrl(path) { return /^https?:\/\//.test(path) ? path : this.$store.state.endpoint.replace(/\/$/, '') + path; },
    duplicateName(entry) { const name = entry.name || {}; return [name.given_name, name.family_name].filter(Boolean).join(' ') || '#' + entry.id; },
    startMerge(id) { this.duplicateToMerge = id; this.mergeDialog = true; },
    async loadDuplicates() {
      if (!this.info || this.duplicatesLoading) return;
      const id = this.info.id, requestId = this.requestId;
      this.duplicatesLoading = true; this.duplicatesError = '';
      try {
        const { data } = await axios.get(this.$store.state.endpoint + '/person/' + id + '/duplicates');
        if (requestId !== this.requestId) return;
        this.duplicates = data.candidates; this.duplicatesLoaded = true;
      } catch (error) { if (requestId === this.requestId) this.duplicatesError = this.$t('personInfo.error'); }
      finally { if (requestId === this.requestId) this.duplicatesLoading = false; }
    },
    localized(value) { return typeof value === 'object' && value ? (value[this.$i18n.locale] || value.gl || value.es || value.en || '') : value; },
    roleLabel(role) { return this.$te('personInfo.roles.' + role) ? this.$t('personInfo.roles.' + role) : this.$t('personInfo.relatedRole'); },
    date(value) { return value ? new Intl.DateTimeFormat([this.$i18n.locale, 'es-ES']).format(new Date(value + 'T12:00:00')) : this.$t('personInfo.noEndDate'); },
    vinculations(period) { return this.info.vinculations.filter(row => row.period === period); },
    async load(id) {
      if (!id) return;
      const requestId = ++this.requestId;
      this.loading = true; this.error = ''; this.info = null;
      this.duplicates = []; this.duplicatesLoaded = false; this.duplicatesLoading = false; this.duplicatesError = ''; this.duplicateToMerge = null;
      try {
        const { data } = await axios.get(this.$store.state.endpoint + '/person/' + id + '/overview');
        if (requestId === this.requestId) {
          this.info = data;
          if (this.tab === 2) this.loadDuplicates();
        }
      }
      catch (error) { if (requestId === this.requestId) this.error = this.$t('personInfo.error'); }
      finally { if (requestId === this.requestId) this.loading = false; }
    },
    async retry(id) {
      this.retrying = id;
      try {
        const { data } = await axios.post(this.$store.state.endpoint + '/person/merge/' + id + '/retry');
        this.$store.commit('alert', { color: data.ldap_status === 'complete' ? 'success' : 'warning', text: this.$t(data.ldap_status === 'complete' ? 'personInfo.merge.done' : 'personInfo.merge.pending', { username: 'LDAP' }) });
        await this.load(this.info.id);
      } catch (error) { this.error = this.$t('personInfo.error'); }
      finally { this.retrying = null; }
    },
    merged(result) {
      this.mergeDialog = false;
      this.$store.commit('alert', { color: result.ldap_status === 'complete' ? 'success' : 'warning', text: this.$t(result.ldap_status === 'complete' ? 'personInfo.merge.done' : 'personInfo.merge.pending', { username: result.discarded_username || 'LDAP' }) });
      this.$emit('merged', result);
      if (Number(this.personId) === result.target_id) this.load(result.target_id);
    }
  }
};
</script>

<style scoped>
.person-overview-body { min-height: 180px; }
.person-heading { display: flex; align-items: center; gap: 18px; padding-top: 8px; }
.person-heading-text { min-width: 0; overflow-wrap: anywhere; }
.person-duplicate-heading { display: flex; align-items: center; gap: 10px; padding: 8px 0; }
.person-email { display: block; }
.person-account { display: grid; grid-template-columns: minmax(85px, auto) minmax(0, 1fr); gap: 6px 16px; }
.person-account dt { font-weight: 500; }
.person-account dd { margin: 0; overflow-wrap: anywhere; }
.person-vinculation { display: flex; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid #eee; }
.person-vinculation > span { flex-shrink: 0; }
.person-related { display: flex; flex-direction: column; padding: 8px 0; border-bottom: 1px solid #eee; overflow-wrap: anywhere; }
.person-overview-actions { flex-wrap: wrap; gap: 4px; }
@media (max-width: 599px) {
  .person-heading { align-items: flex-start; gap: 12px; }
  .person-vinculation { flex-direction: column; gap: 4px; }
  .person-overview-actions { padding-bottom: max(12px, env(safe-area-inset-bottom)); }
  .person-overview-actions .v-btn { max-width: 100%; }
}
</style>
